import { buildMemberOrderUrl } from '../hooks/useLiff'
import { offeringUrl } from '../offering/offeringLogic'
import { showCopyTip } from '../lib/showCopyTip'
import {
  actionLabel,
  deadlineNotice,
  orderCardTone,
  formatBaht,
  formatShortDate,
  statusPill,
  stepLabels,
  type MemberOrder,
} from './myOrdersLogic'

const MAX_ITEMS = 3

function Steps({ step, labels }: { step: number; labels: string[] }) {
  return (
    <div className="mo-steps" aria-label={`ขั้นตอนปัจจุบัน: ${labels[step]}`}>
      <div className="mo-steps-bar">
        {labels.map((l, i) => (
          <i key={l} className={i <= step ? 'on' : ''} />
        ))}
      </div>
      <div className="mo-steps-lbl">
        {labels.map((l, i) => (
          <span key={l} className={i === step ? 'cur' : ''}>
            {l}
          </span>
        ))}
      </div>
    </div>
  )
}

/** readOnly = แอดมินดูมุมมองลูกค้า — ซ่อนปุ่มจ่าย/กรอก กันแอดมินทำแทนลูกค้าโดยไม่ตั้งใจ */
export function MyOrderCard({ order, readOnly = false, showTracking = true, now = Date.now() }: { order: MemberOrder; readOnly?: boolean; showTracking?: boolean; now?: number }) {
  const pill = statusPill(order)
  const action = readOnly ? null : actionLabel(order)
  const deadline = deadlineNotice(order, now)
  const cancelled = order.step < 0
  const shown = order.items.slice(0, MAX_ITEMS)
  const more = order.items.length - shown.length
  const showSteps = order.group === 'active' && !order.actionNeeded && order.step >= 0

  const copyTracking = async () => {
    try {
      await navigator.clipboard.writeText(order.trackingNumber)
      showCopyTip('คัดลอกเลขพัสดุแล้ว')
    } catch {
      /* clipboard ถูกบล็อก */
    }
  }

  return (
    <article className={`mo-glass mo-card mo-card--${orderCardTone(order)}${cancelled ? ' is-cancel' : ''}${deadline?.warning ? ' is-urgent' : ''}`} data-order-id={order.orderId}>
      <div className="mo-row">
        <span className={`mo-pill mo-pill--${pill.tone}`}>{pill.label}</span>
        {order.orderType === 'ceremony' && <span className="mo-tag">พิธี</span>}
        {order.orderType === 'offering' && <span className="mo-tag mo-tag--offering">🙏 ถวายทรัพย์</span>}
        <span className="mo-oid">#{order.orderId}</span>
        <span className="mo-date">{formatShortDate(order)}</span>
      </div>
      {deadline && <div className="mo-row mo-row-deadline"><span className={`mo-deadline${deadline.warning ? ' is-warning' : ''}`} role="note">
        <span className="mo-deadline-badge"><i className="fas fa-clock" aria-hidden="true" /> {deadline.due}</span>
        {deadline.warning && <strong className="mo-deadline-badge"><i className="fas fa-triangle-exclamation" aria-hidden="true" /> {deadline.warning}</strong>}
      </span></div>}

      <p className="mo-items">
        {shown.map((it, i) => (
          <span key={`${it.name}-${i}`} className="mo-item">
            <b>{it.name}</b>
            {it.qty > 1 ? ` × ${it.qty}` : ''}
          </span>
        ))}
        {more > 0 && <span className="mo-item">และอีก {more} รายการ</span>}
      </p>

      {showSteps && <Steps step={order.step} labels={stepLabels(order.orderType)} />}

      {showTracking && order.trackingNumber && (
        <div className="mo-trk">
          <span>
            📦 {order.carrier || 'เลขพัสดุ'} · {order.trackingNumber}
          </span>
          <span className="mo-trk-act">
            <button type="button" onClick={copyTracking}>
              คัดลอก
            </button>
            {order.trackingUrl && (
              <>
                {' · '}
                <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer">
                  ติดตาม
                </a>
              </>
            )}
          </span>
        </div>
      )}

      <div className="mo-foot">
        <span className="mo-sum">
          <small>รวม</small> <span className="mo-total">{formatBaht(order.total)}</span>
          {cancelled && <span className="mo-note"> (ไม่นับยอดสะสม)</span>}
        </span>
        {action && (
          <a className={`mo-cta mo-cta--${order.actionNeeded || 'view'}`} href={order.orderType === 'offering' ? offeringUrl(order.botId || '1', order.orderId) : buildMemberOrderUrl(order.orderId, order.botId || '1', order.linkKey || '')}>
            {action} →
          </a>
        )}
      </div>
    </article>
  )
}
